import type { FieldValue } from '@bmo/form-core';

import type { WorkflowField } from '@/api/workflows';

import { isMaskedRule } from './fieldValidation';

/**
 * What a free-text answer becomes when the advisor leaves the box: the same text without leading
 * or trailing whitespace (QA AH-007 — '  John  ' was kept, prefilled onward and printed).
 *
 * Returns `value` itself — the same reference — whenever there is nothing to do, so a caller can
 * write back only on a real change:
 *   - anything but a ShortText / LongText, or a value that is not a string;
 *   - a read-only field, whose value was prefilled and is not the advisor's to reshape;
 *   - a sin / phone / postal rule, whose mask owns the stored text;
 *   - a display mask, whose stored value is already bare digits.
 *
 * Only the ends: spaces INSIDE the text are an answer's own business (a regex rule's, if anyone's).
 */
export function trimOnBlur(
  field: Pick<WorkflowField, 'type' | 'readOnly' | 'validate' | 'displayMask'>,
  value: FieldValue,
): FieldValue {
  if (field.type !== 'ShortText' && field.type !== 'LongText') return value;
  if (typeof value !== 'string' || field.readOnly) return value;
  if (isMaskedRule(field.validate) || field.displayMask) return value;
  const trimmed = value.trim();
  return trimmed === value ? value : trimmed;
}
